import { formatNumberAsWhole } from './commonFunction';
import { NumericEngine, Raw } from './numericEngine';

export const DISCOUNT_SOURCE = {
    PERCENTAGE: 'percentage',
    AMOUNT: 'amount',
};

/**
 * Infer discountSource from API response when discountSource is missing (legacy records).
 */
export const inferDiscountSourceFromResponse = ({ discountSource, membershipProgramId, discountPercentage, discount }) => {
    if (discountSource === DISCOUNT_SOURCE.PERCENTAGE || discountSource === DISCOUNT_SOURCE.AMOUNT) {
        return discountSource;
    }
    if (membershipProgramId) {
        return DISCOUNT_SOURCE.PERCENTAGE;
    }
    const pct = Number(discountPercentage) || 0;
    const amt = Number(discount) || 0;
    if (pct > 0) {
        return DISCOUNT_SOURCE.PERCENTAGE;
    }
    if (amt > 0) {
        return DISCOUNT_SOURCE.AMOUNT;
    }
    return DISCOUNT_SOURCE.PERCENTAGE;
};

/**
 * Resolve discount amount and percentage from subtotal and source of truth.
 */
export const resolveDiscount = ({
    subTotal,
    discountSource = DISCOUNT_SOURCE.PERCENTAGE,
    discountAmount = 0,
    discountPercentage = 0,
    programPercentage = null,
}) => {
    const sub = Number(subTotal) || 0;

    if (sub === 0) {
        return {
            discountValue: '0',
            discountPercentage: 0,
            discountSource: discountSource || DISCOUNT_SOURCE.PERCENTAGE,
        };
    }

    let source = discountSource;
    let pct = Number(discountPercentage) || 0;
    let value = '0';

    if (programPercentage != null && programPercentage !== '') {
        source = DISCOUNT_SOURCE.PERCENTAGE;
        pct = Number(programPercentage) || 0;
        value = NumericEngine.divide(Raw.mul(sub, pct), 100);
    } else if (source === DISCOUNT_SOURCE.AMOUNT) {
        const amount = Number(discountAmount) || 0;
        value = String(amount);
        if (amount > 0) {
            pct = Number(NumericEngine.divide(Raw.mul(amount, 100), sub));
        } else {
            pct = 0;
        }
    } else {
        source = DISCOUNT_SOURCE.PERCENTAGE;
        pct = Number(discountPercentage) || 0;
        if (pct > 0) {
            value = NumericEngine.divide(Raw.mul(sub, pct), 100);
        } else {
            const amount = Number(discountAmount) || 0;
            value = amount > 0 ? String(amount) : '0';
            pct = 0;
        }
    }

    return {
        discountValue: value,
        discountPercentage: pct,
        discountSource: source,
    };
};

/**
 * Hydrate discount fields from GET response.
 */
export const hydrateDiscountFromResponse = ({
    subTotal,
    discount = 0,
    discountPercentage: respDiscountPercentage,
    discountSource: respDiscountSource,
    membershipProgramId = null,
    programPercentage = null,
}) => {
    const source = inferDiscountSourceFromResponse({
        discountSource: respDiscountSource,
        membershipProgramId,
        discountPercentage: respDiscountPercentage ?? programPercentage,
        discount,
    });

    return resolveDiscount({
        subTotal,
        discountSource: source,
        discountAmount: discount,
        discountPercentage: respDiscountPercentage ?? programPercentage ?? 0,
        programPercentage: membershipProgramId ? programPercentage : null,
    });
};

/** Formatted discount amount for read-only detail summaries */
export const formatDiscountAmountDisplay = ({ discount, discountPercentage, discountSource }) => {
    const formattedAmount = NumericEngine.format(discount || 0, { formatCurrency: true, trailingZeros: true });
    const pct = Number(discountPercentage) || 0;
    if (discountSource === DISCOUNT_SOURCE.AMOUNT) {
        return formattedAmount;
    }
    // if (pct > 0) {
    //     return `${formatNumberAsWhole(pct)}% - ${formattedAmount}`;
    // }
    return formattedAmount;
};

/**
 * Estimate detail (Draft / Sent / Finalized): show authoritative value first, then derived from subtotal.
 * - amount: stored $, then calculated % (discount / subTotal * 100)
 * - percentage: stored %, then calculated $ (subTotal * pct / 100)
 * Membership: program % and stored discount amount.
 */
export const formatEstimateDiscountDetailDisplay = ({ discount, discountPercentage, discountSource, subTotal, membershipProgram }) => {
    const sub = Number(subTotal) || 0;
    const amt = Number(discount) || 0;
    const storedPct = Number(discountPercentage) || 0;

    if (membershipProgram?.programName || membershipProgram?._id) {
        const progPct = Number(membershipProgram?.discount) || 0;
        const formattedAmount = NumericEngine.format(amt, { formatCurrency: true, trailingZeros: true });
        // if (progPct > 0) {
        //     return `${formatNumberAsWhole(progPct)}% - ${formattedAmount}`;
        // }
        return formattedAmount;
    }

    const source = inferDiscountSourceFromResponse({
        discountSource,
        membershipProgramId: null,
        discountPercentage: storedPct,
        discount: amt,
    });

    if (source === DISCOUNT_SOURCE.AMOUNT) {
        const amountStr = NumericEngine.format(amt, { formatCurrency: true, trailingZeros: true });
        if (sub > 0 && amt > 0) {
            const derivedPct = Number(NumericEngine.divide(Raw.mul(amt, 100), sub));
            return `${amountStr} - ${formatNumberAsWhole(derivedPct)}%`;
        }
        return amountStr;
    }

    const pctToShow = storedPct;
    const derivedAmt = sub > 0 && pctToShow > 0 ? NumericEngine.divide(Raw.mul(sub, pctToShow), 100) : amt > 0 ? String(amt) : '0';
    const amtStr = NumericEngine.format(Number(derivedAmt) || 0, { formatCurrency: true, trailingZeros: true });
    if (pctToShow > 0) {
        return `${formatNumberAsWhole(pctToShow)}% - ${amtStr}`;
    }
    return amtStr;
};
